/**
 * v132 — SetManager.
 *
 * Renders the set list under the active capture area and the "Start new set"
 * CTA. The last set is expanded by default; earlier sets collapse.
 *
 * Inline editor for label / setupDelta / notes on the open set.
 */
import { useState } from 'react';
import type { RichShot } from '../v2-schema';
import type { ShotSet } from '../v3-schema';
import { Plus, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import clsx from 'clsx';
import { computeSetAggregates } from '../aggregates';

interface SetManagerProps {
  sets: readonly ShotSet[];
  shots: readonly RichShot[];
  onStartNewSet: (opts: { label?: string; setupDelta?: string; notes?: string }) => void;
  onPatchCurrentSet: (p: Partial<Pick<ShotSet, 'label' | 'setupDelta' | 'notes'>>) => void;
}

export function SetManager({ sets, shots, onStartNewSet, onPatchCurrentSet }: SetManagerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const last = sets[sets.length - 1];
    return new Set(last ? [last.id] : []);
  });
  const [newSetPrompt, setNewSetPrompt] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDelta, setNewDelta] = useState('');
  const [editingCurrent, setEditingCurrent] = useState(false);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const shotsById = new Map(shots.map((s) => [s.id, s]));
  const current = sets[sets.length - 1];

  const submitNewSet = () => {
    onStartNewSet({
      label: newLabel || undefined,
      setupDelta: newDelta || undefined,
    });
    setNewLabel('');
    setNewDelta('');
    setNewSetPrompt(false);
  };

  return (
    <div className="space-y-2">
      {/* CTA — start a new set */}
      {!newSetPrompt && current && current.shotIds.length > 0 && (
        <button
          onClick={() => setNewSetPrompt(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 min-h-[44px] border border-dashed border-ember-500/40 text-ember-500 rounded-md hover:bg-ember-500/5 touch-manipulation"
        >
          <Plus size={14} /> Start a new set
        </button>
      )}

      {/* New-set prompt */}
      {newSetPrompt && (
        <div className="space-y-2 bg-zinc-900/40 border border-ember-500/30 rounded-md p-3">
          <p className="text-[11px] font-mono uppercase tracking-wider text-ember-500">New set</p>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder='Set label (optional) — e.g. "After +1/4 turn limb"'
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
            autoFocus
          />
          <input
            value={newDelta}
            onChange={(e) => setNewDelta(e.target.value)}
            placeholder='What changed? (optional) — e.g. "Bumped left limb bolt 1/4 turn down"'
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { setNewSetPrompt(false); setNewLabel(''); setNewDelta(''); }}
              className="flex-1 py-2 min-h-[44px] text-[12px] font-mono uppercase tracking-wider text-zinc-400 hover:text-zinc-200 border border-zinc-800 rounded touch-manipulation"
            >Cancel</button>
            <button
              onClick={submitNewSet}
              className="flex-1 py-2 min-h-[44px] text-[12px] font-mono uppercase tracking-wider bg-ember-500 text-zinc-950 rounded touch-manipulation"
            >Begin set</button>
          </div>
        </div>
      )}

      {/* Sets list */}
      {sets.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            Sets ({sets.length})
          </p>
          {sets.map((set, idx) => {
            const isOpen = expanded.has(set.id);
            const isCurrent = idx === sets.length - 1;
            const setShots = set.shotIds
              .map((id) => shotsById.get(id))
              .filter((s): s is RichShot => Boolean(s));
            const aggs = computeSetAggregates(setShots);
            const label = set.label || `Set ${idx + 1}`;
            return (
              <div
                key={set.id}
                className={clsx(
                  'border rounded',
                  isCurrent
                    ? 'border-ember-500/40 bg-ember-500/5'
                    : 'border-zinc-800 bg-zinc-900/30',
                )}
              >
                <button
                  onClick={() => toggle(set.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left touch-manipulation"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span className={clsx(
                      'text-sm font-medium truncate',
                      isCurrent ? 'text-ember-500' : 'text-zinc-200',
                    )}>{label}</span>
                    {isCurrent && (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-ember-500/80 px-1.5 py-0.5 border border-ember-500/40 rounded">Open</span>
                    )}
                  </div>
                  <span className="text-[11px] text-zinc-500 font-mono tabular-nums whitespace-nowrap">
                    {setShots.length} shots · {aggs ? `${aggs.meanRadiusIn.toFixed(2)}″ r` : '—'}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-2">
                    {set.setupDelta && (
                      <p className="text-[12px] text-zinc-400 italic border-l-2 border-zinc-700 pl-2">
                        Δ: {set.setupDelta}
                      </p>
                    )}
                    {set.notes && (
                      <p className="text-[12px] text-zinc-400 whitespace-pre-wrap">{set.notes}</p>
                    )}
                    {aggs && (
                      <div className="grid grid-cols-3 gap-2 text-[11px] font-mono tabular-nums">
                        <div className="bg-zinc-950/50 border border-zinc-800 rounded px-2 py-1">
                          <p className="text-[9px] text-zinc-500 uppercase">Mean r</p>
                          <p className="text-cyan-300">{aggs.meanRadiusIn.toFixed(2)}″</p>
                        </div>
                        <div className="bg-zinc-950/50 border border-zinc-800 rounded px-2 py-1">
                          <p className="text-[9px] text-zinc-500 uppercase">95% r</p>
                          <p className="text-cyan-300">{aggs.group95RadiusIn.toFixed(2)}″</p>
                        </div>
                        <div className="bg-zinc-950/50 border border-zinc-800 rounded px-2 py-1">
                          <p className="text-[9px] text-zinc-500 uppercase">Best</p>
                          <p className="text-cyan-300">{aggs.bestRadiusIn.toFixed(2)}″</p>
                        </div>
                      </div>
                    )}
                    {isCurrent && (
                      <div>
                        {!editingCurrent ? (
                          <button
                            onClick={() => setEditingCurrent(true)}
                            className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                          >
                            <Pencil size={10} /> Edit set label / notes
                          </button>
                        ) : (
                          <div className="space-y-1.5">
                            <input
                              defaultValue={set.label ?? ''}
                              onBlur={(e) => onPatchCurrentSet({ label: e.target.value || undefined })}
                              placeholder="Set label"
                              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[12px]"
                            />
                            <input
                              defaultValue={set.setupDelta ?? ''}
                              onBlur={(e) => onPatchCurrentSet({ setupDelta: e.target.value || undefined })}
                              placeholder="What changed (Δ)"
                              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[12px]"
                            />
                            <textarea
                              defaultValue={set.notes ?? ''}
                              onBlur={(e) => onPatchCurrentSet({ notes: e.target.value || undefined })}
                              placeholder="Set notes"
                              rows={2}
                              className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[12px] resize-none"
                            />
                            <button
                              onClick={() => setEditingCurrent(false)}
                              className="text-[11px] text-zinc-500 hover:text-zinc-300"
                            >Done</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
